import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PublishLayout } from '../components/PublishLayout';
import { Button } from "../components/ui/button";
import { ShareModal } from '../components/ShareModal';
import { usePublish } from '../contexts/PublishContext';
import { useSlackNotification } from '../hooks/useSlackNotification';
import { useAuth } from '../contexts/AuthContext';
import {
  publishShow,
  scheduleShow,
  unpublishShow,
  unScheduleShow,
  fetchShow,
  fetchActiveReleaseStrategies,
  ReleaseStrategy
} from '../services/showService';
import {
  Show,
  ShowState,
  DashTenant,
  TenantRelease,
  platformTypeToTenant,
  tenantDisplayName,
  PlatformType,
  DevicePlatform,
  ALL_DEVICE_PLATFORMS,
  devicePlatformDisplayName,
  findTenantPlatformRelease,
} from '../types/Show';
import { platformMatrixEnabled } from '../config/Config';
import { auth } from '../lib/firebase';
import { validateShowForPublish, formatValidationErrors, getMissingFieldsSummary } from '../services/showValidationService';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Calendar,
  Globe,
  Play,
  Users,
  Share2,
  Link2,
  Clock,
  Calendar as CalendarIcon,
  ChevronRight,
  ChevronRightCircle,
  CheckCircle2,
  AlertCircle,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { ScheduleCalendar } from '../components/ScheduleCalendar';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';

// Sample data
const regions = [
  { id: 'global', name: 'Global' },
  { id: 'na', name: 'North America' },
  { id: 'eu', name: 'Europe' },
  { id: 'asia', name: 'Asia' },
  { id: 'sa', name: 'South America' }
];

const changes = [
  { type: 'details', message: 'Updated show details' },
  { type: 'thumbnails', message: 'Added 2 new thumbnails' },
  { type: 'content', message: 'Uploaded 3 episodes' },
  { type: 'extras', message: 'Added post-credit thumbnails' }
];

const stateBadgeVariant = (state: ShowState | undefined) => {
  switch (state) {
    case ShowState.PUBLISHED: return 'default' as const;
    case ShowState.SCHEDULED: return 'secondary' as const;
    default: return 'outline' as const;
  }
};

const stateLabel = (state: ShowState | undefined) => {
  switch (state) {
    case ShowState.PUBLISHED: return 'Published';
    case ShowState.SCHEDULED: return 'Scheduled';
    case ShowState.UNPUBLISHED: return 'Not Published';
    default: return 'Not Published';
  }
};

function PublishPreview() {
  const navigate = useNavigate();
  const { showId } = useParams<{ showId: string }>();
  const { state, updateState } = usePublish();
  const { user, profile } = useAuth();
  const {
    sendPublishRequest,
    sendUnpublishRequest,
    sendScheduleRequest,
    sendUnscheduleRequest,
    sendPlatformReleaseRequest,
    sendPlatformWithdrawRequest,
    loading: slackLoading
  } = useSlackNotification();
  const [activeTab, setActiveTab] = useState('preview');
  const [selectedRegions, setSelectedRegions] = useState<string[]>(['global']);
  const [publishDate, setPublishDate] = useState<Date | null>(null);
  const [scheduleSlots, setScheduleSlots] = useState<Array<{ date: Date; time: string }>>([]);
  const [showShareModal, setShowShareModal] = useState(false);
  const [publishFrequency, setPublishFrequency] = useState(7);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showData, setShowData] = useState<Show | null>(null);
  const [loadingShow, setLoadingShow] = useState(false);

  // Per-tenant state
  const [tenantScheduleMode, setTenantScheduleMode] = useState<Record<DashTenant, boolean>>({} as any);
  const [tenantScheduleSlots, setTenantScheduleSlots] = useState<Record<DashTenant, Array<{ date: Date; time: string }>>>({} as any);
  const [tenantPublishing, setTenantPublishing] = useState<Record<DashTenant, boolean>>({} as any);
  const [tenantStaggerRelease, setTenantStaggerRelease] = useState<Record<DashTenant, boolean>>({} as any);
  const [tenantReleaseStrategies, setTenantReleaseStrategies] = useState<Record<DashTenant, string>>({} as any);
  // Device platforms the next action applies to, per tenant. Undefined ⇒ all four.
  const [tenantSelectedPlatforms, setTenantSelectedPlatforms] = useState<Partial<Record<DashTenant, DevicePlatform[]>>>({});
  // Reminder that a request is awaiting Slack approval (state only changes after a human approves).
  const [tenantPendingNote, setTenantPendingNote] = useState<Partial<Record<DashTenant, string>>>({});
  const [releaseStrategies, setReleaseStrategies] = useState<ReleaseStrategy[]>([]);
  const [loadingReleaseStrategies, setLoadingReleaseStrategies] = useState(false);
  const [releaseStrategiesError, setReleaseStrategiesError] = useState<string | null>(null);

  // Derive tenant list from platformTypes
  const tenants: DashTenant[] = (showData?.platformTypes || [])
    .map(pt => platformTypeToTenant[pt])
    .filter((t): t is DashTenant => t !== undefined);

  const hasTenantSupport = tenants.length > 0;

  const defaultReleaseStrategy = releaseStrategies.find(strategy => strategy.isDefault) || releaseStrategies[0];
  const defaultReleaseStrategyId = defaultReleaseStrategy?.id;

  const getSelectedReleaseStrategyId = (tenant: DashTenant): string | undefined => {
    return tenantReleaseStrategies[tenant] || defaultReleaseStrategyId;
  };

  const getSelectedReleaseStrategy = (tenant: DashTenant): ReleaseStrategy | undefined => {
    const selectedStrategyId = getSelectedReleaseStrategyId(tenant);
    return releaseStrategies.find(strategy => strategy.id === selectedStrategyId);
  };

  // Legacy tenant-level lookup (drives the card when the platform matrix is disabled)
  const getTenantRelease = (tenant: DashTenant): TenantRelease | undefined => {
    return showData?.tenantReleases?.find(tr => tr.tenant === tenant);
  };

  const getPlatformRelease = (tenant: DashTenant, platform: DevicePlatform): TenantRelease | undefined =>
    findTenantPlatformRelease(showData?.tenantReleases, tenant, platform);

  const getPlatformState = (tenant: DashTenant, platform: DevicePlatform): ShowState =>
    getPlatformRelease(tenant, platform)?.state || ShowState.UNPUBLISHED;

  // Tenant header badge: any-platform PUBLISHED > SCHEDULED > UNPUBLISHED
  // (mirrors the backend's deriveTopLevelFields precedence).
  const getDerivedTenantState = (tenant: DashTenant): ShowState => {
    const states = ALL_DEVICE_PLATFORMS.map(p => getPlatformState(tenant, p));
    if (states.includes(ShowState.PUBLISHED)) return ShowState.PUBLISHED;
    if (states.includes(ShowState.SCHEDULED)) return ShowState.SCHEDULED;
    return ShowState.UNPUBLISHED;
  };

  const getSelectedPlatforms = (tenant: DashTenant): DevicePlatform[] =>
    tenantSelectedPlatforms[tenant] ?? ALL_DEVICE_PLATFORMS;

  const togglePlatform = (tenant: DashTenant, platform: DevicePlatform) => {
    setTenantSelectedPlatforms(prev => {
      const current = prev[tenant] ?? ALL_DEVICE_PLATFORMS;
      const next = current.includes(platform)
        ? current.filter(p => p !== platform)
        : [...current, platform];
      return { ...prev, [tenant]: next };
    });
  };

  const platformListLabel = (platforms: DevicePlatform[]): string =>
    platforms.length === ALL_DEVICE_PLATFORMS.length
      ? 'all platforms'
      : platforms.map(p => devicePlatformDisplayName[p]).join(', ');

  const handleTenantPublish = async (tenant: DashTenant, platforms?: DevicePlatform[]) => {
    if (!showId || !showData) return;

    if (platformMatrixEnabled && (!platforms || platforms.length === 0)) {
      toast.error('Select at least one platform to publish to');
      return;
    }

    const validationResult = validateShowForPublish(showData);
    if (!validationResult.isValid) {
      const missingFields = getMissingFieldsSummary(validationResult.errors);
      toast.error(`Cannot publish. Missing required fields: ${missingFields}`, { duration: 5000 });
      return;
    }

    const userName = user?.displayName || profile?.full_name || user?.email || 'Unknown User';
    const userId = user?.uid || profile?.id || 'unknown';
    const showName = state.details?.title || showData.name || 'Untitled Show';
    const isScheduling = tenantScheduleMode[tenant];
    const isStaggerRelease = tenantStaggerRelease[tenant] === true;
    const releaseStrategy = isStaggerRelease ? getSelectedReleaseStrategyId(tenant) : null;
    const platformNames = platforms?.map(p => p as string);
    const targetLabel = platforms?.length
      ? `${tenantDisplayName[tenant]} · ${platformListLabel(platforms)}`
      : tenantDisplayName[tenant];

    if (isStaggerRelease && !releaseStrategy) {
      toast.error(releaseStrategiesError || 'No active release strategy is available');
      return;
    }

    setTenantPublishing(prev => ({ ...prev, [tenant]: true }));
    const toastId = `tenant-publish-${tenant}`;

    try {
      if (isScheduling) {
        const slots = tenantScheduleSlots[tenant] || [];
        let scheduledDate: string;
        if (slots.length > 0) {
          const firstSlot = slots[0];
          const [hours, minutes] = firstSlot.time.split(':');
          const selectedDateTime = new Date(firstSlot.date);
          selectedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          scheduledDate = selectedDateTime.toISOString();
        } else {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(12, 0, 0, 0);
          scheduledDate = tomorrow.toISOString();
        }

        toast.loading(`Scheduling ${targetLabel}...`, { id: toastId });
        await sendPlatformReleaseRequest(showId, showName, userName, userId, tenant.toLowerCase(), scheduledDate, releaseStrategy, platformNames);
        toast.success(`Schedule request sent for ${targetLabel} - ${new Date(scheduledDate).toLocaleString()}`, { id: toastId });
      } else {
        toast.loading(`Publishing to ${targetLabel}...`, { id: toastId });
        await sendPlatformReleaseRequest(showId, showName, userName, userId, tenant.toLowerCase(), undefined, releaseStrategy, platformNames);
        toast.success(`Publish request sent for ${targetLabel}!`, { id: toastId });
      }
      setTenantPendingNote(prev => ({
        ...prev,
        [tenant]: `${isScheduling ? 'Schedule' : 'Publish'} request for ${platforms?.length ? platformListLabel(platforms) : 'this app'} is awaiting Slack approval — the status below updates once approved.`,
      }));

      // Refetch show data to update UI
      const updatedShow = await fetchShow(showId, {
        requestedMetadata: ['LANGUAGE', 'MATURITY_RATING'],
        includeEpisodes: false,
        includeEpisodeUserMapping: false
      });
      setShowData(updatedShow);
    } catch (error) {
      console.error(`Error publishing ${tenant}:`, error);
      toast.error(`Failed to publish ${tenantDisplayName[tenant]}. Please try again.`, { id: toastId });
    } finally {
      setTenantPublishing(prev => ({ ...prev, [tenant]: false }));
    }
  };

  const handleTenantUnpublish = async (tenant: DashTenant, platforms?: DevicePlatform[]) => {
    if (!showId) return;

    if (platformMatrixEnabled && (!platforms || platforms.length === 0)) {
      toast.error('Select at least one platform to unpublish from');
      return;
    }

    const userName = user?.displayName || profile?.full_name || user?.email || 'Unknown User';
    const userId = user?.uid || profile?.id || 'unknown';
    const showName = state.details?.title || showData?.name || 'Untitled Show';
    const platformNames = platforms?.map(p => p as string);
    const targetLabel = platforms?.length
      ? `${tenantDisplayName[tenant]} · ${platformListLabel(platforms)}`
      : tenantDisplayName[tenant];

    const toastId = `tenant-unpublish-${tenant}`;
    try {
      toast.loading(`Unpublishing from ${targetLabel}...`, { id: toastId });
      await sendPlatformWithdrawRequest(showId, showName, userName, userId, tenant.toLowerCase(), platformNames);
      toast.success(`Unpublish request sent for ${targetLabel}!`, { id: toastId });
      setTenantPendingNote(prev => ({
        ...prev,
        [tenant]: `Unpublish request for ${platforms?.length ? platformListLabel(platforms) : 'this app'} is awaiting Slack approval — the status below updates once approved.`,
      }));

      // Refetch show data
      const updatedShow = await fetchShow(showId, {
        requestedMetadata: ['LANGUAGE', 'MATURITY_RATING'],
        includeEpisodes: false,
        includeEpisodeUserMapping: false
      });
      setShowData(updatedShow);
    } catch (error) {
      console.error(`Error unpublishing ${tenant}:`, error);
      toast.error(`Failed to unpublish ${tenantDisplayName[tenant]}.`, { id: toastId });
    }
  };

  // Legacy handlers for shows without tenantReleases
  const handlePublish = async () => {
    if (!showId) {
      toast.error('Show ID is required to publish');
      return;
    }

    if (showData) {
      const validationResult = validateShowForPublish(showData);
      if (!validationResult.isValid) {
        const missingFields = getMissingFieldsSummary(validationResult.errors);
        toast.error(`Cannot publish show. Missing required fields: ${missingFields}`, {
          duration: 5000,
          description: 'Please fill in all required fields in the Show Details section before publishing.'
        });
        console.error('Validation errors:', validationResult.errors);
        return;
      }
    } else {
      toast.error('Show data not loaded. Please refresh the page and try again.');
      return;
    }

    const userName = user?.displayName || profile?.full_name || user?.email || 'Unknown User';
    const userId = user?.uid || profile?.id || 'unknown';
    const showName = state.details?.title || 'Untitled Show';

    setIsPublishing(true);
    const toastId = 'publish-progress';

    try {
      if (publishDate) {
        toast.loading('Scheduling your show...', { id: toastId });

        let scheduledDate: string;
        if (scheduleSlots.length > 0) {
          const firstSlot = scheduleSlots[0];
          const [hours, minutes] = firstSlot.time.split(':');
          const selectedDateTime = new Date(firstSlot.date);
          selectedDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          scheduledDate = selectedDateTime.toISOString();
        } else {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          tomorrow.setHours(12, 0, 0, 0);
          scheduledDate = tomorrow.toISOString();
        }

        try {
          await sendScheduleRequest(showId, showName, userName, userId, scheduledDate);
        } catch (slackError) {
          console.warn('Failed to send Slack notification for schedule:', slackError);
        }

        toast.success(`Schedule request sent successfully for ${new Date(scheduledDate).toLocaleString()}!`, { id: toastId });
      } else {
        toast.loading('Publishing your show...', { id: toastId });

        try {
          await sendPublishRequest(showId, showName, userName, userId);
          toast.success('Publish request sent successfully! Show will be published shortly.', { id: toastId });
        } catch (slackError) {
          console.warn('Failed to send Slack notification for publish:', slackError);
          toast.error('Failed to send publish request. Please try again.', { id: toastId });
        }

        setTimeout(() => {
          const publishUrl = `${window.location.origin}/show/${showId}`;
          navigate('/', {
            state: {
              showPublishSuccess: true,
              publishUrl,
              contentType: state.contentType || 'dashreel'
            }
          });
        }, 1500);
      }
    } catch (error) {
      console.error('Error publishing show:', error);
      toast.error('Failed to publish show. Please try again.', { id: toastId });
    } finally {
      setIsPublishing(false);
    }
  };

  const handleUnpublish = async () => {
    if (!showId) {
      toast.error('Show ID is required');
      return;
    }

    const userName = user?.displayName || profile?.full_name || user?.email || 'Unknown User';
    const userId = user?.uid || profile?.id || 'unknown';
    const showName = state.details?.title || 'Untitled Show';

    try {
      toast.loading('Unpublishing show...');
      try {
        await sendUnpublishRequest(showId, showName, userName, userId);
        toast.success('Unpublish request sent successfully! Show will be unpublished shortly.');
      } catch (slackError) {
        console.warn('Failed to send Slack notification for unpublish:', slackError);
        toast.error('Failed to send unpublish request. Please try again.');
      }
    } catch (error) {
      console.error('Error unpublishing show:', error);
      toast.error('Failed to unpublish show. Please try again.');
    }
  };

  // Fetch show data to check current state
  useEffect(() => {
    const loadShowData = async () => {
      if (!showId) return;

      try {
        setLoadingShow(true);

        await new Promise((resolve) => {
          const unsubscribe = auth.onAuthStateChanged((user) => {
            unsubscribe();
            resolve(user);
          });
        });

        if (!auth.currentUser) {
          console.error('User not authenticated');
          return;
        }

        const show = await fetchShow(showId, {
          requestedMetadata: [
            'LANGUAGE',
            'MATURITY_RATING'
          ],
          includeEpisodes: false,
          includeEpisodeUserMapping: false
        });
        setShowData(show);
      } catch (error) {
        console.error('Error fetching show data:', error);
      } finally {
        setLoadingShow(false);
      }
    };

    loadShowData();
  }, [showId]);

  useEffect(() => {
    const loadReleaseStrategies = async () => {
      try {
        setLoadingReleaseStrategies(true);
        setReleaseStrategiesError(null);

        const strategies = await fetchActiveReleaseStrategies();
        const sortedStrategies = [...(strategies || [])].sort((a, b) => {
          if (a.isDefault === b.isDefault) return 0;
          return a.isDefault ? -1 : 1;
        });

        setReleaseStrategies(sortedStrategies);
      } catch (error) {
        console.error('Error fetching release strategies:', error);
        setReleaseStrategies([]);
        setReleaseStrategiesError('Release strategies could not be loaded');
      } finally {
        setLoadingReleaseStrategies(false);
      }
    };

    loadReleaseStrategies();
  }, []);

  // Check show state to determine button behavior (legacy)
  const isShowPublished = showData?.state === ShowState.PUBLISHED;
  const isShowUnpublished = showData?.state === ShowState.UNPUBLISHED;
  const isShowScheduled = showData?.state === ShowState.SCHEDULED;
  const canSchedule = isShowUnpublished || isShowScheduled;

  useEffect(() => {
    if (publishDate && !canSchedule) {
      setPublishDate(null);
      setScheduleSlots([]);
    }
  }, [canSchedule, publishDate]);

  const handleUnschedule = async () => {
    if (!showId) {
      toast.error('Show ID is required');
      return;
    }

    const userName = user?.displayName || profile?.full_name || user?.email || 'Unknown User';
    const userId = user?.uid || profile?.id || 'unknown';
    const showName = state.details?.title || 'Untitled Show';

    try {
      toast.loading('Unscheduling show...');
      try {
        await sendUnscheduleRequest(showId, showName, userName, userId);
        toast.success('Unschedule request sent successfully! Show will be unscheduled shortly.');
        setPublishDate(null);
        setScheduleSlots([]);
      } catch (slackError) {
        console.warn('Failed to send Slack notification for unschedule:', slackError);
        toast.error('Failed to send unschedule request. Please try again.');
      }
    } catch (error) {
      console.error('Error unscheduling show:', error);
      toast.error('Failed to unschedule show. Please try again.');
    }
  };

  // Render a per-tenant publish card (per-platform matrix inside when enabled)
  const renderTenantCard = (tenant: DashTenant) => {
    const legacyRelease = getTenantRelease(tenant);
    const tenantState = platformMatrixEnabled
      ? getDerivedTenantState(tenant)
      : (legacyRelease?.state || ShowState.UNPUBLISHED);

    const selectedPlatforms = getSelectedPlatforms(tenant);
    // Selected platforms the next Publish/Schedule can act on (anything not already published)
    const publishablePlatforms = platformMatrixEnabled
      ? selectedPlatforms.filter(p => getPlatformState(tenant, p) !== ShowState.PUBLISHED)
      : [];
    // Selected platforms Unpublish can act on
    const withdrawablePlatforms = platformMatrixEnabled
      ? selectedPlatforms.filter(p => {
          const s = getPlatformState(tenant, p);
          return s === ShowState.PUBLISHED || s === ShowState.SCHEDULED;
        })
      : [];

    const isPublished = tenantState === ShowState.PUBLISHED;
    const isScheduled = tenantState === ShowState.SCHEDULED;
    const isUnpublished = tenantState === ShowState.UNPUBLISHED || (!platformMatrixEnabled && !legacyRelease);
    const canTenantSchedule = platformMatrixEnabled
      ? publishablePlatforms.length > 0
      : (isUnpublished || isScheduled);
    const showUnpublishButton = platformMatrixEnabled
      ? withdrawablePlatforms.length > 0
      : (isPublished || isScheduled);
    const isScheduleMode = tenantScheduleMode[tenant] || false;
    const isBusy = tenantPublishing[tenant] || false;
    const isStaggerRelease = tenantStaggerRelease[tenant] || false;
    const selectedReleaseStrategy = getSelectedReleaseStrategy(tenant);
    const selectedReleaseStrategyId = selectedReleaseStrategy?.id;
    const allSelected = selectedPlatforms.length === ALL_DEVICE_PLATFORMS.length;

    const releasedAtDate = !platformMatrixEnabled && legacyRelease?.releasedAt
      ? new Date(legacyRelease.releasedAt)
      : null;

    return (
      <div key={tenant} className="rounded-lg border bg-card">
        {/* Card Header */}
        <div className="p-4 border-b">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold">{tenantDisplayName[tenant]}</h3>
            <Badge variant={stateBadgeVariant(tenantState)}>
              {stateLabel(tenantState)}
            </Badge>
          </div>
          {releasedAtDate && (
            <p className="text-xs text-muted-foreground mt-1">
              {isScheduled ? 'Scheduled for: ' : 'Released: '}
              {releasedAtDate.toLocaleDateString()} at {releasedAtDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>

        {/* Card Body */}
        <div className="p-4 space-y-4">
          {/* Per-platform release matrix */}
          {platformMatrixEnabled && (
            <div className="rounded-md border">
              <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/50">
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Platforms
                </Label>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline"
                  onClick={() =>
                    setTenantSelectedPlatforms(prev => ({
                      ...prev,
                      [tenant]: allSelected ? [] : ALL_DEVICE_PLATFORMS,
                    }))
                  }
                >
                  {allSelected ? 'Clear selection' : 'Select all'}
                </button>
              </div>
              <div className="divide-y divide-border">
                {ALL_DEVICE_PLATFORMS.map(platform => {
                  const row = getPlatformRelease(tenant, platform);
                  const rowState = row?.state || ShowState.UNPUBLISHED;
                  const rowDate = row?.releasedAt ? new Date(row.releasedAt) : null;
                  const isChecked = selectedPlatforms.includes(platform);
                  const isTenantWideRow = !!row && !row.platform;

                  return (
                    <div key={platform} className="flex items-center gap-3 px-3 py-2">
                      <Checkbox
                        id={`platform-${tenant}-${platform}`}
                        checked={isChecked}
                        onCheckedChange={() => togglePlatform(tenant, platform)}
                      />
                      <Label
                        htmlFor={`platform-${tenant}-${platform}`}
                        className="text-sm flex-1 cursor-pointer"
                      >
                        {devicePlatformDisplayName[platform]}
                      </Label>
                      {rowDate && (
                        <span className="text-xs text-muted-foreground">
                          {rowState === ShowState.SCHEDULED ? 'Scheduled ' : 'Released '}
                          {rowDate.toLocaleDateString()}{' '}
                          {rowDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      )}
                      <Badge
                        variant={stateBadgeVariant(rowState)}
                        title={isTenantWideRow ? 'From an app-wide release (predates per-platform releases)' : undefined}
                      >
                        {stateLabel(rowState)}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Immediate / Scheduled toggle — only for unpublished or scheduled tenants */}
          {canTenantSchedule && (
            <div className="flex items-center justify-between">
              <Label className="text-sm">Publish mode</Label>
              <div className="flex gap-3">
                <div
                  className="relative cursor-pointer"
                  onClick={() => setTenantScheduleMode(prev => ({ ...prev, [tenant]: false }))}
                >
                  <div className={`px-3 py-1.5 rounded-md border-2 transition-all text-xs font-medium ${
                    !isScheduleMode
                      ? 'border-primary text-primary'
                      : 'border-transparent bg-secondary hover:border-primary/40'
                  }`}>
                    Immediate
                  </div>
                </div>
                <div
                  className="relative cursor-pointer"
                  onClick={() => setTenantScheduleMode(prev => ({ ...prev, [tenant]: true }))}
                >
                  <div className={`px-3 py-1.5 rounded-md border-2 transition-all text-xs font-medium ${
                    isScheduleMode
                      ? 'border-primary text-primary'
                      : 'border-transparent bg-secondary hover:border-primary/40'
                  }`}>
                    Scheduled
                  </div>
                </div>
              </div>
            </div>
          )}

          {canTenantSchedule && (
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor={`stagger-release-${tenant}`} className="text-sm">
                  Stagger this release
                </Label>
                <p className="text-xs text-muted-foreground">
                  Choose a release strategy for this platform.
                </p>
              </div>
              <Checkbox
                id={`stagger-release-${tenant}`}
                checked={isStaggerRelease}
                onCheckedChange={(checked) => {
                  setTenantStaggerRelease(prev => ({ ...prev, [tenant]: checked === true }));
                }}
              />
            </div>
          )}

          {canTenantSchedule && isStaggerRelease && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-4">
                <Label className="text-sm">Release strategy</Label>
                <Select
                  value={selectedReleaseStrategyId}
                  onValueChange={(strategyId) => {
                    setTenantReleaseStrategies(prev => ({ ...prev, [tenant]: strategyId }));
                  }}
                  disabled={loadingReleaseStrategies || releaseStrategies.length === 0}
                >
                  <SelectTrigger className="w-[220px]">
                    <SelectValue placeholder={loadingReleaseStrategies ? 'Loading...' : 'No strategies'} />
                  </SelectTrigger>
                  <SelectContent>
                    {releaseStrategies.map((strategy) => (
                      <SelectItem key={strategy.id} value={strategy.id}>
                        {strategy.name}{strategy.isDefault ? ' (Default)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground">
                {selectedReleaseStrategy?.description ||
                  releaseStrategiesError ||
                  (releaseStrategies.length === 0 && !loadingReleaseStrategies
                    ? 'No active release strategies configured'
                    : 'Select the episode unlock strategy for this platform')}
              </p>
            </div>
          )}

          {/* Schedule calendar — only when schedule mode is on */}
          {isScheduleMode && canTenantSchedule && (
            <div className="space-y-2">
              <Label className="text-xs">Pick date and time</Label>
              <div className="border rounded-lg bg-card p-3 overflow-hidden">
                <ScheduleCalendar
                  frequency={1}
                  onChange={(slots) => {
                    setTenantScheduleSlots(prev => ({ ...prev, [tenant]: slots }));
                  }}
                />
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-2">
            {canTenantSchedule && (
              <Button
                className="flex-1"
                onClick={() => handleTenantPublish(tenant, platformMatrixEnabled ? publishablePlatforms : undefined)}
                disabled={isBusy || slackLoading || loadingShow || loadingReleaseStrategies}
              >
                {isBusy
                  ? 'Processing...'
                  : `${isScheduleMode ? 'Schedule' : 'Publish Now'}${
                      platformMatrixEnabled && !allSelected ? ` (${publishablePlatforms.length})` : ''
                    }`}
              </Button>
            )}

            {showUnpublishButton && (
              <Button
                variant="destructive"
                className="flex-1"
                onClick={() => handleTenantUnpublish(tenant, platformMatrixEnabled ? withdrawablePlatforms : undefined)}
                disabled={isBusy || slackLoading}
              >
                {platformMatrixEnabled
                  ? `Unpublish${!allSelected ? ` (${withdrawablePlatforms.length})` : ''}`
                  : (isScheduled ? 'Unschedule' : 'Unpublish')}
              </Button>
            )}
          </div>

          {platformMatrixEnabled && !canTenantSchedule && !showUnpublishButton && (
            <p className="text-xs text-muted-foreground">
              Select at least one platform above to publish or unpublish.
            </p>
          )}

          {tenantPendingNote[tenant] && (
            <p className="text-xs text-amber-600">
              {tenantPendingNote[tenant]}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <PublishLayout
      onContinue={!hasTenantSupport ? handlePublish : undefined}
      continueLabel={isPublishing ? 'Publishing...' : (publishDate ? 'Schedule' : 'Publish')}
      showContinue={!hasTenantSupport}
    >
      <div className="max-w-[720px] mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-base font-semibold">Review & Publish</h2>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)}>
                <Users className="h-4 w-4 mr-2" />
                Invite
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  toast.success('Link copied to clipboard');
                }}
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="flex gap-8">
            <div className="flex-1 space-y-6">
              {/* Summary Block */}
              {/*<div className="rounded-lg border bg-card p-4">*/}
              {/*  <div>*/}
              {/*    <div className="flex items-center justify-between">*/}
              {/*      <div>*/}
              {/*        <h3 className="text-lg font-medium">{state.details.title || 'Empty'}</h3>*/}
              {/*        <p className="text-sm text-muted-foreground">*/}
              {/*          Total Episodes: 3 | 0 Live | 3 Draft*/}
              {/*        </p>*/}
              {/*      </div>*/}
              {/*      <div className="flex gap-1">*/}
              {/*        {hasTenantSupport ? (*/}
              {/*          tenants.map(t => (*/}
              {/*            <Badge key={t} variant="secondary">{tenantDisplayName[t]}</Badge>*/}
              {/*          ))*/}
              {/*        ) : (*/}
              {/*          <Badge variant="secondary">Dashreel</Badge>*/}
              {/*        )}*/}
              {/*      </div>*/}
              {/*    </div>*/}
              {/*    <div className="flex items-center justify-between mt-4 pt-4 border-t">*/}
              {/*      <div className="flex items-center gap-2">*/}
              {/*        <Globe className="h-4 w-4 text-muted-foreground" />*/}
              {/*        <span className="text-sm text-muted-foreground">Publishing to:</span>*/}
              {/*        <span className="text-sm">{regions.find(r => r.id === selectedRegions[0])?.name}</span>*/}
              {/*      </div>*/}
              {/*      <div className="flex items-center gap-2">*/}
              {/*        <Calendar className="h-4 w-4 text-muted-foreground" />*/}
              {/*        <span className="text-sm text-muted-foreground">Status:</span>*/}
              {/*        <span className="text-sm">*/}
              {/*          {hasTenantSupport*/}
              {/*            ? tenants.map(t => {*/}
              {/*                const r = getTenantRelease(t);*/}
              {/*                return `${tenantDisplayName[t]}: ${stateLabel(r?.state)}`;*/}
              {/*              }).join(' | ')*/}
              {/*            : (publishDate ? 'Scheduled' : 'Immediate')*/}
              {/*          }*/}
              {/*        </span>*/}
              {/*      </div>*/}
              {/*    </div>*/}
              {/*  </div>*/}
              {/*</div>*/}

              {/* Changes List */}
              <div className="rounded-lg border bg-card">
                <div className="px-4 py-3 border-b">
                  <h3 className="text-sm font-medium">List of Changes</h3>
                </div>
                {changes.length > 0 ? (
                  <div className="divide-y divide-border">
                    {state.details && (
                      <div className="px-4 py-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-sm font-medium">Updated Show Details</h4>
                            <ul className="mt-1 space-y-1">
                              <li className="text-sm text-muted-foreground">• Updated title</li>
                              <li className="text-sm text-muted-foreground">• Updated description</li>
                              <li className="text-sm text-muted-foreground">• Changed genres</li>
                            </ul>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="float-right ml-4"
                            onClick={() => navigate(`/publish/${showId}`)}
                          >
                            View
                            <ChevronRight className="ml-1 h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}

                    <div className="px-4 py-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-sm font-medium">Updated Content</h4>
                          <ul className="mt-1 space-y-1">
                            <li className="text-sm text-muted-foreground">• Added 2 episodes</li>
                            <li className="text-sm text-muted-foreground">• Removed 1 episode</li>
                            <li className="text-sm text-muted-foreground">• Updated 1 thumbnail</li>
                          </ul>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="float-right ml-4"
                          onClick={() => navigate(`/publish/${showId}/content`)}
                        >
                          View
                          <ChevronRight className="ml-1 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="px-4 py-8 text-center">
                    <p className="text-sm text-muted-foreground">No changes have been made</p>
                  </div>
                )}
              </div>

              {/* Per-Tenant Publish Cards */}
              {hasTenantSupport ? (
                <div className="space-y-4">
                  <Label className="text-sm font-semibold">Publishing</Label>
                  {tenants.map(tenant => renderTenantCard(tenant))}
                </div>
              ) : (
                <>
                  {/* Legacy single publish UI */}
                  <div className="flex items-center justify-between gap-8">
                    <div className="flex-1">
                      <Label>Publish Time</Label>
                      <p className="text-sm text-muted-foreground mt-1">
                        Choose when to publish your content
                        {!canSchedule && (
                          <span className="block text-xs text-orange-600 mt-1">
                            Scheduling is only available for unpublished or already scheduled shows
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-4">
                      <div
                        className="relative cursor-pointer group"
                        onClick={() => setPublishDate(null)}
                      >
                        <div className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          !publishDate
                            ? 'border-primary text-primary'
                            : 'border-transparent bg-secondary hover:border-primary/40'
                        }`}>
                          <span className="text-sm font-medium">Immediate</span>
                        </div>
                      </div>
                      <div
                        className={`relative cursor-pointer group ${
                          !canSchedule ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                        onClick={() => canSchedule && setPublishDate(new Date())}
                      >
                        <div className={`px-4 py-2 rounded-lg border-2 transition-all ${
                          publishDate
                            ? 'border-primary text-primary'
                            : 'border-transparent bg-secondary hover:border-primary/40'
                        } ${
                          !canSchedule ? 'hover:border-transparent hover:bg-secondary' : ''
                        }`}>
                          <span className="text-sm font-medium">Scheduled</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {publishDate && canSchedule && (
                    <div className="rounded-lg border bg-card">
                      <div className="flex items-center justify-between p-4 border-b">
                        <div>
                          <Label className="font-medium">Schedule Options</Label>
                          <p className="text-sm text-muted-foreground mt-1">Configure your publishing schedule</p>
                        </div>
                      </div>

                      <div className="p-4 space-y-6">
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label className="flex items-center gap-1">
                              Set free to watch episodes
                              <span className="text-primary">*</span>
                            </Label>
                            <Input
                              type="number"
                              min="0"
                              defaultValue="5"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-1">
                              Starting Episodes
                              <span className="text-primary">*</span>
                            </Label>
                            <Input
                              type="number"
                              min="1"
                              max="100"
                              defaultValue="20"
                              className="h-9"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label className="flex items-center gap-1">
                              Weekly release frequency
                              <span className="text-primary">*</span>
                            </Label>
                            <Input
                              type="number"
                              min="1"
                              max="30"
                              value={publishFrequency}
                              onChange={(e) => setPublishFrequency(parseInt(e.target.value) || 1)}
                              className="h-9"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label>Pick specific days and times for release</Label>
                          <div className="border rounded-lg bg-card p-4 overflow-hidden">
                            <ScheduleCalendar
                              frequency={publishFrequency}
                              onChange={(slots) => {
                                console.log('Selected slots:', slots);
                                setScheduleSlots(slots);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-4">
                    <Button
                      variant="secondary"
                      className="flex-1"
                      onClick={() => toast.success('Draft saved successfully')}
                    >
                      Save for Later
                    </Button>
                    <Button
                      variant={isShowPublished ? "secondary" : "default"}
                      className={isShowPublished ? "flex-1 opacity-60 cursor-not-allowed" : "flex-1"}
                      onClick={handlePublish}
                      disabled={!!isPublishing || !!slackLoading || !!isShowPublished || !!loadingShow || (!!publishDate && !canSchedule)}
                    >
                      {isPublishing ? 'Processing...' : (publishDate ? 'Schedule Show' : 'Publish Now')}
                    </Button>
                  </div>

                  {publishDate && (
                    <div className="flex gap-3 pt-2">
                      <Button
                        variant="outline"
                        className="flex-1"
                        onClick={handleUnschedule}
                        disabled={slackLoading}
                      >
                        Unschedule
                      </Button>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant={isShowUnpublished ? "secondary" : "destructive"}
                      className={isShowUnpublished ? "flex-1 opacity-60 cursor-not-allowed" : "flex-1"}
                      onClick={handleUnpublish}
                      disabled={slackLoading || isShowUnpublished}
                    >
                      Unpublish Show
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Empty container */}
          <div className="h-[128px]" />
        </div>
      <ShareModal open={showShareModal} onOpenChange={setShowShareModal} />
    </PublishLayout>
  );
}

export default PublishPreview;
